/*
 * HLTH Free Gift with Purchase
 *
 * Approach: intercept fetch to /cart/add. Parse the request body (handles single-item,
 * multi-item items[], JSON, FormData, URLSearchParams). If any item being added belongs
 * to the trigger product (HLTH Code Complete Meal), append the gift as an additional
 * item in the SAME request. One round-trip, one drawer render, atomic at the server.
 *
 * Why fetch-level (not form-level) interception: Loop's subscription widget reads the
 * flavor-quantity selector and constructs the multi-variant items[] payload at submit
 * time. If we intercept the form submit before Loop runs, we drop everything except
 * the form's [name="id"] (a single variant). Hooking fetch instead lets Loop build
 * the full payload, then we just augment it.
 *
 * Don't-double-add: sessionStorage flag keyed to cart.token. Once we gift a cart, we
 * don't gift it again — even if the user removes the ebook. The flag clears when the
 * trigger leaves the cart, or when the cart token rotates after checkout.
 *
 * Auto-remove: a small pub/sub listener watches cart updates. If the trigger is gone
 * but the gift remains, the gift is stripped via /cart/change and the flag clears.
 */
(function () {
  const config = window.HLTH_FREE_GIFT;
  if (!config || !config.giftProductId || !config.triggerProductId) return;

  const SOURCE = 'hlth-free-gift';
  const TRIGGER_PRODUCT_ID = Number(config.triggerProductId);
  const GIFT_PRODUCT_ID = Number(config.giftProductId);

  let giftVariantId = Number(config.giftVariantId) || 0;
  const triggerVariantIds = new Set();
  if (Array.isArray(config.triggerVariantIds)) {
    config.triggerVariantIds.forEach((id) => {
      const n = Number(id);
      if (n) triggerVariantIds.add(n);
    });
  }
  let lastKnownCartToken = '';
  let internal = false;
  let triggerVariantsPromise = null;
  let giftVariantPromise = null;

  function ensureTriggerVariants() {
    if (triggerVariantIds.size > 0) return Promise.resolve();
    if (!config.triggerHandle) return Promise.resolve();
    if (!triggerVariantsPromise) {
      internal = true;
      triggerVariantsPromise = fetch(`/products/${config.triggerHandle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (p && p.variants) p.variants.forEach((v) => triggerVariantIds.add(Number(v.id)));
        })
        .catch(() => {})
        .finally(() => { internal = false; });
    }
    return triggerVariantsPromise;
  }

  function ensureGiftVariant() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    if (!config.giftHandle) return Promise.resolve(0);
    if (!giftVariantPromise) {
      internal = true;
      giftVariantPromise = fetch(`/products/${config.giftHandle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (p && p.variants && p.variants.length) giftVariantId = p.variants[0].id;
          return giftVariantId;
        })
        .catch(() => 0)
        .finally(() => { internal = false; });
    }
    return giftVariantPromise;
  }

  function fetchCartToken() {
    internal = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        lastKnownCartToken = c.token || lastKnownCartToken;
        return c;
      })
      .catch(() => null)
      .finally(() => { internal = false; });
  }

  // sessionStorage opt-out flag
  const ADDED_KEY = 'hlth_free_gift_added_for_token';
  function getAddedToken() {
    try { return sessionStorage.getItem(ADDED_KEY); } catch (e) { return null; }
  }
  function setAddedToken(token) {
    try { sessionStorage.setItem(ADDED_KEY, token || ''); } catch (e) { /* noop */ }
  }
  function clearAddedToken() {
    try { sessionStorage.removeItem(ADDED_KEY); } catch (e) { /* noop */ }
  }

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function getSectionsList() {
    const cart = getCartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((s) => s.id);
    }
    return [];
  }

  function publishUpdate(data, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: SOURCE,
        productVariantId: variantId,
        cartData: data,
      });
    }
  }

  // ---- Body parsing: support FormData, URLSearchParams, JSON, plain object, string ----
  function entriesFromIterable(iter) {
    const out = {};
    for (const [k, v] of iter) out[k] = v;
    return out;
  }

  function parseFormEntries(entries) {
    const items = [];
    if ('id' in entries) {
      const item = { id: Number(entries.id), quantity: Number(entries.quantity || 1) };
      if (entries.selling_plan) item.selling_plan = entries.selling_plan;
      const props = {};
      Object.keys(entries).forEach((k) => {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = entries[k];
      });
      if (Object.keys(props).length) item.properties = props;
      items.push(item);
    } else {
      const multiMap = {};
      Object.keys(entries).forEach((k) => {
        const m = k.match(/^items\[(\d+)\]\[(\w+)\]$/);
        if (m) {
          const [, idx, field] = m;
          (multiMap[idx] = multiMap[idx] || {})[field] = entries[k];
          return;
        }
        const m2 = k.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);
        if (m2) {
          const [, idx, propKey] = m2;
          const slot = (multiMap[idx] = multiMap[idx] || {});
          (slot.properties = slot.properties || {})[propKey] = entries[k];
        }
      });
      Object.keys(multiMap).sort((a, b) => Number(a) - Number(b)).forEach((idx) => {
        const m = multiMap[idx];
        if (!m.id) return;
        const item = { id: Number(m.id), quantity: Number(m.quantity || 1) };
        if (m.selling_plan) item.selling_plan = m.selling_plan;
        if (m.properties) item.properties = m.properties;
        items.push(item);
      });
    }
    return {
      items,
      sections: entries.sections,
      sections_url: entries.sections_url,
    };
  }

  function normalizeJsonBody(parsed) {
    const items = [];
    if (parsed && typeof parsed === 'object') {
      if (parsed.id != null) {
        const item = { id: Number(parsed.id), quantity: Number(parsed.quantity || 1) };
        if (parsed.selling_plan) item.selling_plan = parsed.selling_plan;
        if (parsed.properties) item.properties = parsed.properties;
        items.push(item);
      } else if (Array.isArray(parsed.items)) {
        parsed.items.forEach((it) => {
          if (!it || it.id == null) return;
          const item = { id: Number(it.id), quantity: Number(it.quantity || 1) };
          if (it.selling_plan) item.selling_plan = it.selling_plan;
          if (it.properties) item.properties = it.properties;
          items.push(item);
        });
      }
    }
    return {
      items,
      sections: parsed && parsed.sections,
      sections_url: parsed && parsed.sections_url,
    };
  }

  async function parseBody(body) {
    if (!body) return null;
    if (body instanceof FormData) return parseFormEntries(entriesFromIterable(body.entries()));
    if (body instanceof URLSearchParams) return parseFormEntries(entriesFromIterable(body.entries()));
    if (typeof body === 'string') {
      try { return normalizeJsonBody(JSON.parse(body)); } catch (e) {}
      try { return parseFormEntries(entriesFromIterable(new URLSearchParams(body).entries())); } catch (e) {}
      return null;
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      try { return await parseBody(await body.text()); } catch (e) { return null; }
    }
    if (typeof body === 'object') {
      // Plain object — treat as JSON-equivalent
      return normalizeJsonBody(body);
    }
    return null;
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }
  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  // Build the augmented init payload (always JSON for simplicity, since /cart/add accepts it).
  function buildAugmentedInit(originalInit, parsed, gv) {
    const items = parsed.items.slice();
    items.push({ id: gv, quantity: 1 });

    let sections = parsed.sections;
    let sectionsUrl = parsed.sections_url;
    if (!sections) {
      const list = getSectionsList();
      if (list.length) {
        sections = list.join(',');
        sectionsUrl = window.location.pathname;
      }
    }

    const payload = { items };
    if (sections) {
      payload.sections = sections;
      payload.sections_url = sectionsUrl;
    }

    return Object.assign({}, originalInit || {}, {
      method: 'POST',
      headers: Object.assign({}, (originalInit && originalInit.headers) || {}, {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      }),
      body: JSON.stringify(payload),
    });
  }

  // ---- Augmenting fetch wrapper ----
  const origFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';

    // Pass-through for non-cart-mutation calls and our own internal calls.
    if (!isCartMutation(url) || internal) {
      return origFetch(input, init);
    }

    if (isCartAdd(url)) {
      // Try to augment the body. Bail out cleanly on any error.
      let augmented = null;
      try {
        const opts = init || {};
        let body = opts.body;
        // Request objects: avoid the body-stream complexity, just pass through.
        if (input instanceof Request && body == null) {
          augmented = null;
        } else {
          const parsed = await parseBody(body);
          if (parsed && parsed.items && parsed.items.length) {
            const hasTrigger = parsed.items.some((i) => triggerVariantIds.has(Number(i.id)));
            const alreadyHasGift = parsed.items.some((i) => Number(i.id) === giftVariantId);
            const alreadyGiftedThisCart = lastKnownCartToken && getAddedToken() === lastKnownCartToken;

            if (hasTrigger && !alreadyHasGift && !alreadyGiftedThisCart) {
              const gv = giftVariantId || (await ensureGiftVariant());
              if (gv) {
                augmented = { url, init: buildAugmentedInit(opts, parsed, gv) };
              }
            }
          }
        }
      } catch (e) {
        console.error('[hlth-free-gift] body augment failed', e);
        augmented = null;
      }

      if (augmented) {
        try {
          internal = true;
          const response = await origFetch(augmented.url, augmented.init);
          internal = false;

          if (response.ok) {
            // Refresh cart token + flag.
            const cart = await fetchCartToken();
            if (cart && cart.token) setAddedToken(cart.token);

            // Render the new cart sections + notify Dawn pub/sub so the icon bubble updates.
            try {
              const data = await response.clone().json();
              const cartEl = getCartElement();
              if (cartEl && data.sections && typeof cartEl.renderContents === 'function') {
                try { cartEl.renderContents(data); } catch (err) { /* noop */ }
              }
              publishUpdate(data);
            } catch (err) { /* noop */ }
          }

          return response;
        } catch (err) {
          internal = false;
          console.error('[hlth-free-gift] augmented fetch failed, falling back', err);
          // Fall through to the original request below
        }
      }
    }

    // Default path (no augmentation): forward unchanged. Schedule a post-mutation sync
    // so the auto-remove path catches cart removes that didn't come through us.
    const promise = origFetch(input, init);
    promise.then(() => { syncGiftAfterCartUpdate({ source: 'native-fetch' }); }).catch(() => {});
    return promise;
  };

  // ---- Auto-remove: when trigger leaves the cart, strip the gift ----
  let removingGift = false;
  async function syncGiftAfterCartUpdate(event) {
    if (event && event.source === SOURCE) return;
    if (removingGift || internal) return;
    try {
      const cart = await fetchCartToken();
      if (!cart) return;
      const items = cart.items || [];
      const trigger = items.find((i) => Number(i.product_id) === TRIGGER_PRODUCT_ID);
      const gift = items.find((i) => Number(i.product_id) === GIFT_PRODUCT_ID);

      if (!trigger && gift) {
        removingGift = true;
        const sections = getSectionsList();
        const body = { id: gift.key, quantity: 0 };
        if (sections.length) {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }
        internal = true;
        const res = await origFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });
        internal = false;
        const data = await res.json();
        clearAddedToken();
        const cartEl = getCartElement();
        if (cartEl && data.sections && typeof cartEl.renderContents === 'function') {
          try { cartEl.renderContents(data); } catch (err) { /* noop */ }
        }
        publishUpdate(data);
      } else if (!trigger && !gift) {
        clearAddedToken();
      } else if (trigger && gift && cart.token) {
        // Both present — keep flag fresh in case storage was cleared.
        setAddedToken(cart.token);
      }
    } catch (err) {
      console.error('[hlth-free-gift] sync error', err);
    } finally {
      removingGift = false;
      internal = false;
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncGiftAfterCartUpdate);
  }

  // ---- Init: pre-load variant IDs + cart token in parallel ----
  function init() {
    ensureTriggerVariants();
    ensureGiftVariant();
    fetchCartToken();
  }
  init();
})();
